import React, { useState, useEffect } from "react";
import {
  FaStar,
  FaTrophy,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaChartBar,
  FaColumns,
} from "react-icons/fa";
import axiosInstance from "../config/axiosInstance";
import { API } from "../config/config";
import SortableTh from "./SortableTh";
import DriverRideStatsCards, {
  emptyDriverStatusBreakdown,
  DRIVER_CARD_FILTER_LABELS,
} from "./DriverRideStatsCards";

const isMeaningfulExperience = (value) => {
  if (value === null || value === undefined) return false;
  const normalized = String(value).trim().toLowerCase();
  return (
    normalized !== "" &&
    normalized !== "n/a" &&
    normalized !== "na" &&
    normalized !== "none" &&
    normalized !== "null" &&
    normalized !== "undefined" &&
    normalized !== "-"
  );
};

const getStartOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addYears = (date, years) =>
  new Date(date.getFullYear() + years, date.getMonth(), date.getDate());

const addMonths = (date, months) =>
  new Date(date.getFullYear(), date.getMonth() + months, date.getDate());

const formatExperienceFromIssueDate = (dateOfIssue) => {
  if (!dateOfIssue) return "N/A";

  const parsedIssueDate = new Date(dateOfIssue);
  if (Number.isNaN(parsedIssueDate.getTime())) return "N/A";

  const issueDate = getStartOfDay(parsedIssueDate);
  const today = getStartOfDay(new Date());

  if (issueDate > today) return "N/A";

  let years = today.getFullYear() - issueDate.getFullYear();
  if (addYears(issueDate, years) > today) years -= 1;

  let cursorDate = addYears(issueDate, years);
  let months =
    (today.getFullYear() - cursorDate.getFullYear()) * 12 +
    (today.getMonth() - cursorDate.getMonth());
  if (addMonths(cursorDate, months) > today) months -= 1;

  cursorDate = addMonths(cursorDate, months);
  const oneDayMs = 24 * 60 * 60 * 1000;
  const days = Math.floor((today - cursorDate) / oneDayMs);

  const parts = [];
  if (years > 0) parts.push(`${years} year${years === 1 ? "" : "s"}`);
  if (months > 0) parts.push(`${months} month${months === 1 ? "" : "s"}`);
  if (days > 0) parts.push(`${days} day${days === 1 ? "" : "s"}`);

  return parts.length > 0 ? parts.join(" ") : "0 days";
};

/** Maps common API/DB variants to completed | cancelled | other */
const normalizeRideStatus = (status) => {
  const s = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (s === "canceled" || s === "cancelled") return "cancelled";
  if (s === "complete" || s === "completed") return "completed";
  return s;
};

/** Matches v2 rideController: driverActions Cancelled for this driver, or cancelledBy Driver while assigned to this driver. */
const getDriverCancelAction = (ride, driverIdStr) => {
  if (!ride?.driverActions?.length || !driverIdStr) return null;
  return ride.driverActions.find(
    (a) =>
      a.action === "Cancelled" &&
      a.driverId &&
      String(a.driverId) === String(driverIdStr)
  );
};

const hasDriverAcceptedAction = (ride, driverIdStr) => {
  if (!ride?.driverActions?.length || !driverIdStr) return false;
  return ride.driverActions.some(
    (a) =>
      a.action === "Accepted" &&
      a.driverId &&
      String(a.driverId) === String(driverIdStr)
  );
};

const isDriverAcceptThenCancel = (ride, driverIdStr) => {
  if (!ride || !driverIdStr || ride.declinedByDriver) return false;
  if (ride.driverCancelledAfterAccept || ride.cancelledByDriverViaActions) {
    return true;
  }
  const assignedId = ride?.driverId
    ? String(ride.driverId._id || ride.driverId)
    : null;
  if (
    normalizeRideStatus(ride?.status) === "cancelled" &&
    String(ride?.cancelledBy ?? "").trim().toLowerCase() === "driver" &&
    assignedId === String(driverIdStr) &&
    !hasDriverAcceptedAction(ride, driverIdStr) &&
    !getDriverCancelAction(ride, driverIdStr)
  ) {
    return true;
  }
  if (!hasDriverAcceptedAction(ride, driverIdStr)) return false;
  return !!getDriverCancelAction(ride, driverIdStr);
};

const isCustomerCancelledAfterAccept = (ride, driverIdStr) => {
  if (ride?.cancelledByUserAfterAccept) return true;
  const assignedId = ride?.driverId
    ? String(ride.driverId._id || ride.driverId)
    : null;
  const isUserCancelled =
    normalizeRideStatus(ride?.status) === "cancelled" &&
    String(ride?.cancelledBy ?? "").trim().toLowerCase() === "user";
  if (!isUserCancelled || assignedId !== String(driverIdStr)) return false;
  if (getDriverCancelAction(ride, driverIdStr)) return false;
  return (
    hasDriverAcceptedAction(ride, driverIdStr) ||
    !hasDriverAcceptedAction(ride, driverIdStr)
  );
};

const isCustomerCancelledBeforeAccept = (ride, driverIdStr) => {
  if (ride?.cancelledByUserBeforeAssigned) return true;
  if (normalizeRideStatus(ride.status) !== "cancelled") return false;
  if (String(ride?.cancelledBy ?? "").trim().toLowerCase() !== "user") {
    return false;
  }
  const assignedId = ride?.driverId
    ? String(ride.driverId._id || ride.driverId)
    : null;
  if (assignedId === String(driverIdStr)) return false;
  return !hasDriverAcceptedAction(ride, driverIdStr);
};

const isDriverRideCompletedByThisDriver = (ride, driverIdStr) => {
  if (!ride || !driverIdStr) return false;
  if (normalizeRideStatus(ride?.status) !== "completed") return false;
  if (isDriverAcceptThenCancel(ride, driverIdStr)) return false;
  const assignedId = ride?.driverId
    ? String(ride.driverId._id || ride.driverId)
    : null;
  return assignedId === String(driverIdStr);
};

/** Human-readable status for Recent Rides (cancelled → who cancelled). */
const getRideStatusLabelForDriverAnalytics = (ride, driverIdStr) => {
  if (ride?.declinedByDriver) {
    return "Decline by You";
  }
  if (ride?.acceptedByOtherDriver) {
    return "Accepted by Other Driver";
  }
  if (isCustomerCancelledBeforeAccept(ride, driverIdStr)) {
    return "Cancelled by User - Before Driver Assigned";
  }
  if (isDriverAcceptThenCancel(ride, driverIdStr)) {
    return "Cancelled by driver";
  }
  if (isCustomerCancelledAfterAccept(ride, driverIdStr)) {
    return "Cancelled by Customer";
  }
  if (isDriverRideCompletedByThisDriver(ride, driverIdStr)) {
    return "Completed";
  }
  if (normalizeRideStatus(ride.status) === "cancelled") {
    const by = String(ride?.cancelledBy ?? "").trim().toLowerCase();
    if (by === "system") return "Cancelled by system";
    return "Cancelled";
  }
  const raw = String(ride?.status ?? "").trim();
  return raw || "N/A";
};

const getDriverCancelTimestamp = (ride, driverIdStr) => {
  const fromApi = ride?.driverCancelledAt;
  if (fromApi) {
    const d = new Date(fromApi);
    if (!Number.isNaN(d.getTime())) return d;
  }
  const entry = getDriverCancelAction(ride, driverIdStr);
  if (!entry?.timestamp) return null;
  const d = new Date(entry.timestamp);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Completed → completedAt / rideEndTime / updatedAt; Cancelled → cancelledAt / updatedAt; else no terminal time. */
const getRideTerminalDateTime = (ride) => {
  if (!ride) return null;
  const status = normalizeRideStatus(ride.status);
  if (status === "completed") {
    const raw =
      ride.completedAt || ride.rideEndTime || ride.updatedAt || null;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (status === "cancelled") {
    const raw = ride.cancelledAt || ride.updatedAt || null;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/** Terminal time for table/sort: completed/cancelled timestamps, else driver-cancel action time (e.g. ride reopened as Requested). */
const getRideTerminalOrDriverCancelTime = (ride, driverIdStr) => {
  return (
    getRideTerminalDateTime(ride) ||
    getDriverCancelTimestamp(ride, driverIdStr)
  );
};

const getCustomerRatingDisplay = (ride) => {
  const fromDoc = ride?.customerRideRating?.rating;
  if (fromDoc !== null && fromDoc !== undefined && fromDoc !== "") {
    const n = typeof fromDoc === "number" ? fromDoc : Number(fromDoc);
    if (Number.isFinite(n)) return n;
  }
  const fromUser = ride?.userRating?.value;
  if (fromUser !== null && fromUser !== undefined && fromUser !== "") {
    const n = typeof fromUser === "number" ? fromUser : Number(fromUser);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

const getCustomerReviewDisplay = (ride) => {
  const rv = ride?.customerRideRating?.review;
  if (typeof rv === "string" && rv.trim()) return rv.trim();
  const fb = ride?.userRating?.feedback;
  if (typeof fb === "string" && fb.trim()) return fb.trim();
  return "";
};

const getRouteText = (ride) => {
  const pickup =
    typeof (ride.pickupLocation || ride.pickup) === "object"
      ? ride.pickupLocation?.address || ride.pickup?.address || "N/A"
      : ride.pickupLocation || ride.pickup || "N/A";
  const dropoff =
    typeof (ride.dropoffLocation || ride.dropoff) === "object"
      ? ride.dropoffLocation?.address || ride.dropoff?.address || "N/A"
      : ride.dropoffLocation || ride.dropoff || "N/A";
  return { pickup, dropoff };
};

const RIDES_PER_PAGE = 10;

const isJoiningFeePending = (driver) =>
  String(driver?.joiningFeeApplicable || "")
    .trim()
    .toLowerCase() === "pending";

const buildPendingAnalyticsShell = (driver) => {
  const dlIssueDate = driver?.drivingLincenseDetails?.dateOfIssue;
  const fallbackExperience = formatExperienceFromIssueDate(dlIssueDate);
  return {
    basicInfo: {
      rating: driver?.overallRating?.value ?? driver?.rating ?? 0,
      totalRatings: driver?.overallRating?.totalRatings ?? 0,
      performanceScore: 0,
      driverSince: driver?.createdAt
        ? new Date(driver.createdAt).toLocaleDateString()
        : "N/A",
      experience: isMeaningfulExperience(fallbackExperience)
        ? fallbackExperience
        : "N/A",
    },
    availability: null,
    statusBreakdown: emptyDriverStatusBreakdown(),
    recentRides: [],
  };
};

const DriverRideAnalytics = ({ driverId, driver }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ startDate: "", endDate: "" });
  const [filterError, setFilterError] = useState("");
  const [ridesPage, setRidesPage] = useState(1);
  const [ridesPagination, setRidesPagination] = useState(null);
  const [selectedCardFilter, setSelectedCardFilter] = useState(null);
  const [showColumnOptions, setShowColumnOptions] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    customerPhone: true,
    customerName: true,
    finalRoute: true,
    finalFare: true,
    rideStatus: true,
    createdAt: true,
    terminalAt: true,
    rating: true,
    review: true,
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [joiningFeeBlockedByApi, setJoiningFeeBlockedByApi] = useState(false);
  const joiningFeePending =
    isJoiningFeePending(driver) || joiningFeeBlockedByApi;

  // Fetch driver analytics from API
  const fetchDriverAnalytics = async (opts = {}) => {
    if (!driverId) return;
    if (isJoiningFeePending(driver)) {
      setAnalytics(buildPendingAnalyticsShell(driver));
      setRidesPagination({
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        hasNext: false,
        hasPrev: false,
      });
      setJoiningFeeBlockedByApi(true);
      setSelectedCardFilter(null);
      setLoading(false);
      setError(null);
      return;
    }

    const { dateFilters, page: pageArg, cardFilter: cardFilterArg } = opts;
    const activeFilters =
      dateFilters !== undefined ? dateFilters : filters;
    const pageToUse =
      pageArg !== undefined ? pageArg : ridesPage;
    const cardFilterToUse =
      cardFilterArg !== undefined ? cardFilterArg : selectedCardFilter;

    try {
      setLoading(true);
      setError(null);
      setFilterError("");

      const params = {
        includeRides: "true",
        page: pageToUse,
        limit: RIDES_PER_PAGE,
      };
      if (activeFilters.startDate) params.startDate = activeFilters.startDate;
      if (activeFilters.endDate) params.endDate = activeFilters.endDate;
      if (cardFilterToUse) params.cardFilter = cardFilterToUse;

      const activeSort = opts.sortConfig ?? sortConfig;
      if (activeSort?.key) {
        params.sortBy = activeSort.key;
        params.sortOrder = activeSort.direction || "asc";
      }

      // Fetch driver analytics data
      const response = await axiosInstance.get(
        `${API.ADMIN.DRIVER_ANALYTICS}/${driverId}`,
        { params }
      );
      const data = response.data;
      if (
        String(data.joiningFeeApplicable || "")
          .trim()
          .toLowerCase() === "pending"
      ) {
        setJoiningFeeBlockedByApi(true);
        setAnalytics({
          ...buildPendingAnalyticsShell(driver || data.driver),
          statusBreakdown:
            data.analytics?.statusBreakdown || emptyDriverStatusBreakdown(),
          recentRides: [],
        });
        setRidesPagination(
          data.pagination ?? {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            hasNext: false,
            hasPrev: false,
          }
        );
        setSelectedCardFilter(null);
        return;
      }
      setJoiningFeeBlockedByApi(false);
      const overview = data.analytics?.overview || data;
      const dlIssueDate = driver?.drivingLincenseDetails?.dateOfIssue;
      const fallbackExperience = formatExperienceFromIssueDate(dlIssueDate);
      const experience = isMeaningfulExperience(data.experience)
        ? data.experience
        : fallbackExperience;

      const listDriverRating =
        data.driver?.overallRating?.value ??
        data.rating ??
        driver?.overallRating?.value ??
        null;
      const profileRating = data.driver?.rating ?? driver?.rating ?? 0;
      const totalRatingsCount =
        data.totalRatings ??
        data.driver?.overallRating?.totalRatings ??
        overview.totalRatings ??
        driver?.overallRating?.totalRatings ??
        0;
      const avgFromRides =
        overview.averageUserRating != null &&
          !Number.isNaN(overview.averageUserRating)
          ? Math.round(overview.averageUserRating * 100) / 100
          : null;
      const displayRating =
        listDriverRating != null && listDriverRating !== ""
          ? Number(listDriverRating)
          : data.rating != null && data.rating !== undefined
            ? data.rating
            : avgFromRides != null && totalRatingsCount > 0
              ? avgFromRides
              : profileRating;

      setAnalytics({
        basicInfo: {
          rating: displayRating,
          totalRatings: totalRatingsCount,
          performanceScore: data.performanceScore ?? 0,
          driverSince: driver?.createdAt
            ? new Date(driver.createdAt).toLocaleDateString()
            : "N/A",
          experience,
        },
        availability: data.availability || null,
        statusBreakdown:
          data.analytics?.statusBreakdown || emptyDriverStatusBreakdown(),
        recentRides: data.recentRides || [],
      });
      setRidesPagination(data.pagination ?? null);
      setRidesPage(data.pagination?.currentPage ?? pageToUse);
      if (cardFilterArg !== undefined) {
        setSelectedCardFilter(cardFilterArg || null);
      } else if (data.activeCardFilter !== undefined) {
        setSelectedCardFilter(data.activeCardFilter || null);
      }
    } catch (error) {
      console.error("Error fetching driver analytics:", error);
      setError("Failed to load driver analytics. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when component mounts or driverId / joining-fee status changes
  useEffect(() => {
    setSelectedCardFilter(null);
    setRidesPage(1);
    setJoiningFeeBlockedByApi(false);
    fetchDriverAnalytics({ page: 1, cardFilter: null });
  }, [driverId, driver?.joiningFeeApplicable]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCardClick = (cardKey) => {
    const nextFilter =
      !cardKey || selectedCardFilter === cardKey ? null : cardKey;
    setRidesPage(1);
    fetchDriverAnalytics({ page: 1, cardFilter: nextFilter });
  };

  const handleClearCardFilter = () => {
    setRidesPage(1);
    fetchDriverAnalytics({ page: 1, cardFilter: null });
  };

  const handleFilterChange = (field, value) => {
    setFilterError("");
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    if (!filters.startDate && !filters.endDate) {
      setFilterError("Select at least a start date or an end date.");
      return;
    }
    if (
      filters.startDate &&
      filters.endDate &&
      filters.startDate > filters.endDate
    ) {
      setFilterError("Start date cannot be greater than end date.");
      return;
    }
    setRidesPage(1);
    fetchDriverAnalytics({ dateFilters: filters, page: 1 });
  };

  const handleClearFilters = () => {
    const clearedFilters = { startDate: "", endDate: "" };
    setFilters(clearedFilters);
    setFilterError("");
    setRidesPage(1);
    fetchDriverAnalytics({ dateFilters: clearedFilters, page: 1 });
  };

  const handleSortSet = (key, direction) => {
    const next = { key, direction };
    setSortConfig(next);
    setRidesPage(1);
    fetchDriverAnalytics({ page: 1, sortConfig: next });
  };

  const handleSortLabelClick = (key) => {
    setSortConfig((prev) => {
      const next = {
        key,
        direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
      };
      setRidesPage(1);
      fetchDriverAnalytics({ page: 1, sortConfig: next });
      return next;
    });
  };

  const analyticsSortThClass =
    "px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hover:bg-gray-100 bg-gray-50";

  const analyticsSortTh = (columnKey, label) => (
    <SortableTh
      columnKey={columnKey}
      label={label}
      activeKey={sortConfig.key}
      activeDirection={sortConfig.direction}
      onSortSet={handleSortSet}
      onSortLabelClick={handleSortLabelClick}
      className={analyticsSortThClass}
    />
  );

  const displayRecentRides = Array.isArray(analytics?.recentRides)
    ? analytics.recentRides
    : [];

  const visibleColumnCount = Object.values(visibleColumns).filter(Boolean).length;

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center py-20">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <FaChartBar className="text-6xl text-blue-500 mb-4 mx-auto" />
          <FaSpinner className="animate-spin text-3xl text-blue-500 mb-4 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Loading Ride Analytics
          </h2>
          <p className="text-gray-600 mb-4">
            Please wait while we analyze driver data...
          </p>
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
            <div
              className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
              style={{ animationDelay: "0.1s" }}
            ></div>
            <div
              className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
              style={{ animationDelay: "0.2s" }}
            ></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center py-12">
        <div className="text-red-500 text-lg mb-4">{error}</div>
        <button
          type="button"
          onClick={() => fetchDriverAnalytics()}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex justify-center items-center py-12">
        <span className="text-gray-500 text-lg">
          No analytics data available
        </span>
      </div>
    );
  }

  const { basicInfo, statusBreakdown, availability } = analytics;
  const displayStatusBreakdown = joiningFeePending
    ? emptyDriverStatusBreakdown()
    : statusBreakdown;
  const driverLocationAddress =
    availability?.location?.address?.trim() || null;
  const proximityRadiusKm = availability?.proximityRadiusKm ?? 10;

  const getStatusIcon = (ride, { driverInitiatedCancel = false } = {}) => {
    if (ride?.declinedByDriver) {
      return <FaTimesCircle className="text-red-500" />;
    }
    if (driverInitiatedCancel) {
      return <FaTimesCircle className="text-orange-600" />;
    }
    if (
      driverId &&
      isDriverAcceptThenCancel(ride, driverId)
    ) {
      return <FaTimesCircle className="text-orange-600" />;
    }
    const s = normalizeRideStatus(ride?.status);
    switch (s) {
      case "completed":
        return <FaCheckCircle className="text-green-500" />;
      case "cancelled":
        return <FaTimesCircle className="text-red-500" />;
      case "pending":
        return <FaClock className="text-yellow-500" />;
      default:
        return <FaSpinner className="text-gray-500" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Driver Info Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              {driver?.fullName || "Driver"} - Ride Analytics
            </h2>
            <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
              <span>Driver ID: {driverId}</span>
              <span>
                Status:{" "}
                <span
                  className={`font-semibold ${driver?.status === "Active"
                    ? "text-green-600"
                    : driver?.status === "Verified"
                      ? "text-blue-600"
                      : "text-yellow-600"
                    }`}
                >
                  {driver?.status || "Unknown"}
                </span>
              </span>
              <span>Experience: {basicInfo.experience}</span>
              <span>Member since: {basicInfo.driverSince}</span>
            </div>
            {driverLocationAddress ? (
              <p className="mt-2 text-sm text-gray-600">
                Current location (from driver availability):{" "}
                <span className="font-medium text-gray-800">
                  {driverLocationAddress}
                </span>
                {availability?.locationUsedForMatching ? (
                  <span className="text-gray-500">
                    {" "}
                    — nearby rides within {proximityRadiusKm} km are included
                  </span>
                ) : null}
              </p>
            ) : availability?.locationUsedForMatching === false ? (
              <p className="mt-2 text-sm text-amber-700">
                No saved driver location in driveravailabilities — ride counts
                use assigned/notified/driverActions only (no proximity filter).
              </p>
            ) : null}
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-500">Performance Score</div>
            <div className="text-3xl font-bold text-blue-600">
              {basicInfo.performanceScore}/10
            </div>
          </div>
        </div>
      </div>
      {/* Date filters (other cards — all time unless range is set) */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <p className="text-sm text-gray-600 mb-3">
          Total Rides is the combined unique count and trip amount from all status
          cards below. Click Total Rides (or load the page) to see the full list;
          click any other card to filter the table. Available, Cancelled by User -
          Before Driver Assigned, and Accepted by Other use last 24 hours and 10 km
          proximity; other cards use assigned or interaction rules.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <button
              type="button"
              onClick={handleApplyFilters}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              Apply
            </button>
          </div>
          <div>
            <button
              type="button"
              onClick={handleClearFilters}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
        {filterError && (
          <p className="mt-2 text-sm text-red-600">{filterError}</p>
        )}
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-xs">Rating</p>
              <p className="text-xl font-bold">
                {basicInfo.rating}
              </p>
              {basicInfo.totalRatings > 0 && (
                <p className="text-yellow-200 text-[10px] mt-0.5">
                  {basicInfo.totalRatings} rating
                  {basicInfo.totalRatings === 1 ? "" : "s"} from customer
                  {basicInfo.totalRatings === 1 ? "" : "s"}
                </p>
              )}
            </div>
            <FaStar className="text-2xl text-yellow-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-xs">Performance</p>
              <p className="text-xl font-bold">
                {basicInfo.performanceScore}/10
              </p>
            </div>
            <FaTrophy className="text-2xl text-purple-200" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-4">
        {joiningFeePending && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            Joining fee Applicable is Pending — ride counts are shown as zero
            and the rides list is not available until the fee is paid.
          </p>
        )}
        <DriverRideStatsCards
          stats={displayStatusBreakdown}
          loading={loading}
          selectedCardFilter={joiningFeePending ? null : selectedCardFilter}
          onCardClick={joiningFeePending ? undefined : handleCardClick}
        />
      </div>

      {/* Recent Rides Table — hidden while joining fee is Pending */}
      {!joiningFeePending && (
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-wrap items-center justify-between gap-2 relative">
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Recent Rides</h3>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 text-xs font-medium border border-blue-200">
                  Filter:{" "}
                  {DRIVER_CARD_FILTER_LABELS[selectedCardFilter || "totalRides"] ||
                    selectedCardFilter ||
                    "Total Rides"}
                  {selectedCardFilter ? (
                    <button
                      type="button"
                      onClick={handleClearCardFilter}
                      className="text-blue-600 hover:text-blue-800 font-semibold"
                      aria-label="Clear card filter"
                    >
                      ×
                    </button>
                  ) : null}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Total Rides lists every unique trip from all status cards. Card
                filters narrow the table to one category.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowColumnOptions((v) => !v)}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50"
            >
              <FaColumns className="mr-2" />
              Columns
            </button>
            {showColumnOptions && (
              <div className="absolute right-0 top-full mt-1 py-2 px-3 bg-white border rounded-lg shadow-lg z-20 min-w-[220px]">
                {[
                  ["customerPhone", "Customer Mobile Number"],
                  ["customerName", "Customer Name"],
                  ["finalRoute", "Final Route"],
                  ["finalFare", "Final Fare"],
                  ["rideStatus", "Ride Status"],
                  ["createdAt", "Created Date & Time"],
                  ["terminalAt", "Completed/Cancelled Date and Time"],
                  ["rating", "Rating"],
                  ["review", "Review"],
                ].map(([col, label]) => (
                  <label
                    key={col}
                    className="flex items-center gap-2 py-1 cursor-pointer text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[col]}
                      onChange={(e) =>
                        setVisibleColumns((v) => ({
                          ...v,
                          [col]: e.target.checked,
                        }))
                      }
                      className="rounded"
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm">
              <tr>
                {analyticsSortTh("rideId", "Ride ID")}
                {visibleColumns.customerPhone &&
                  analyticsSortTh("customerPhone", "Customer Mobile Number")}
                {visibleColumns.customerName &&
                  analyticsSortTh("customerName", "Customer Name")}
                {visibleColumns.finalRoute &&
                  analyticsSortTh("finalRoute", "Final Route")}
                {visibleColumns.finalFare &&
                  analyticsSortTh("finalFare", "Final Fare")}
                {visibleColumns.rideStatus &&
                  analyticsSortTh("rideStatus", "Ride Status")}
                {visibleColumns.createdAt &&
                  analyticsSortTh("createdAt", "Created Date & Time")}
                {visibleColumns.terminalAt &&
                  analyticsSortTh(
                    "terminalAt",
                    "Completed/Cancelled Date and Time"
                  )}
                {visibleColumns.rating && analyticsSortTh("rating", "Rating")}
                {visibleColumns.review && analyticsSortTh("review", "Review")}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayRecentRides.length > 0 ? (
                displayRecentRides.map((ride, index) => (
                  <tr
                    key={ride._id || ride.id || index}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {ride._id}
                    </td>
                    {visibleColumns.customerPhone && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {ride.userId?.phone || "N/A"}
                      </td>
                    )}
                    {visibleColumns.customerName && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {ride.userId?.fullName || "N/A"}
                      </td>
                    )}
                    {visibleColumns.finalRoute && (
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>
                          <div className="font-medium">
                            {getRouteText(ride).pickup}
                          </div>
                          <div className="text-gray-500">
                            → {getRouteText(ride).dropoff}
                          </div>
                        </div>
                      </td>
                    )}
                    {visibleColumns.finalFare && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(ride.fare || ride.amount)}
                      </td>
                    )}
                    {visibleColumns.rideStatus && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const statusLabel = getRideStatusLabelForDriverAnalytics(
                            ride,
                            driverId
                          );
                          const norm = normalizeRideStatus(ride.status);
                          const isCancelledRow =
                            norm === "cancelled" ||
                            statusLabel.startsWith("Cancelled") ||
                            statusLabel === "Decline by You";
                          const isDriverCancelLabel =
                            statusLabel === "Cancelled by driver" ||
                            statusLabel === "Cancelled by Driver";
                          const driverCancelNonTerminal =
                            isDriverCancelLabel && norm !== "cancelled";
                          let textClass = "text-yellow-600";
                          if (statusLabel === "Decline by You") {
                            textClass = "text-red-600";
                          } else if (statusLabel === "Accepted by Other Driver") {
                            textClass = "text-violet-600";
                          } else if (
                            statusLabel ===
                            "Cancelled by User - Before Driver Assigned"
                          ) {
                            textClass = "text-rose-600";
                          } else if (isCancelledRow || isDriverCancelLabel) {
                            textClass = driverCancelNonTerminal
                              ? "text-orange-700"
                              : "text-red-600";
                          } else if (norm === "completed") {
                            textClass = "text-green-600";
                          }
                          return (
                            <div className="flex items-start space-x-2">
                              {getStatusIcon(ride, {
                                driverInitiatedCancel: driverCancelNonTerminal,
                              })}
                              <span className={`text-sm font-medium ${textClass}`}>
                                {statusLabel}
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {visibleColumns.createdAt && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          const raw = ride?.createdAt;
                          if (!raw) {
                            return <span className="text-gray-400">N/A</span>;
                          }
                          const created = new Date(raw);
                          if (Number.isNaN(created.getTime())) {
                            return <span className="text-gray-400">N/A</span>;
                          }
                          return (
                            <div>
                              <div>
                                {created.toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </div>
                              <div className="text-xs text-gray-400">
                                {created.toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {visibleColumns.terminalAt && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          const terminal = getRideTerminalOrDriverCancelTime(
                            ride,
                            driverId
                          );
                          if (!terminal) {
                            return <span className="text-gray-400">N/A</span>;
                          }
                          return (
                            <div>
                              <div>
                                {terminal.toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </div>
                              <div className="text-xs text-gray-400">
                                {terminal.toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                    )}
                    {visibleColumns.rating && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          const r = getCustomerRatingDisplay(ride);
                          return r != null ? r : "N/A";
                        })()}
                      </td>
                    )}
                    {visibleColumns.review && (
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                        {(() => {
                          const text = getCustomerReviewDisplay(ride);
                          if (!text) return <span className="text-gray-400">N/A</span>;
                          return (
                            <span className="line-clamp-3" title={text}>
                              {text}
                            </span>
                          );
                        })()}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={1 + visibleColumnCount}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No recent rides found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {ridesPagination && ridesPagination.totalItems > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
            <span className="text-sm text-gray-600">
              Page {ridesPagination.currentPage} of{" "}
              {Math.max(1, ridesPagination.totalPages)} (
              {ridesPagination.totalItems} rides)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={!ridesPagination.hasPrev}
                onClick={() =>
                  fetchDriverAnalytics({
                    page: ridesPagination.currentPage - 1,
                  })
                }
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={!ridesPagination.hasNext}
                onClick={() =>
                  fetchDriverAnalytics({
                    page: ridesPagination.currentPage + 1,
                  })
                }
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default DriverRideAnalytics;
